import { useQuery, ApolloError } from "@apollo/client";
import { createContext, useState, useReducer, useEffect } from "react";
import useLocalStorageState from "use-local-storage-state";
import {
  GET_FIRMA_IDS_AND_EMAIL,
  GET_PAGED_FIRMA_QUERY,
} from "../api/firma/queries";
import { useTableSelection } from "../components/Table/useTableSelection";
import { defaultFirmaColumns } from "../config/firmaConfig";
import useActiveColumns from "../hooks/useActiveColumns";
import { QueryFilter, FirmaFilterKeys } from "../interfaces/filters";
import { PagedFirmaContext } from "../interfaces/pagedContext";
import { queryFilterReducer } from "./reducers/queryFilterReducer";
import { buildFilter } from "../util/queryBuilder";
import { getActiveColumnFields } from "../util/table";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "./FiltersContext";
import {
  IPagedFirmaQuery,
  IPagedFirmaResponse,
  PagedFirmaQueryField,
} from "../interfaces/firma";
import { IColumn, SortBy } from "../interfaces/table";
import { firmaIndexSortOrderKey } from "../util/localStorageKeys";
import { FirmaDto } from "../interfaces/schema";
import { union } from "lodash";

export const PagedFirmaQueryContext = createContext<
  PagedFirmaContext & { emailableIds: Array<string> }
>({
  loading: false,
  error: undefined,
  columns: [],
  setActiveColumns: () => {},
  setQuery: () => {},
  page: 1,
  pagedResponse: undefined,
  nextPage: undefined,
  prevPage: undefined,
  refetch: undefined,
  emailableIds: [],
  selectAllError: undefined,
  clearSelectAllError: () => {},
  selection: {
    selectedIds: [],
    addSelectedIds: () => {},
    clearSelection: () => {},
    removeSelectedIds: () => {},
    selectAll: () => {},
    lastSelectedId: undefined,
    setLastSelectedId: () => {},
  },
});

export const PagedFirmaQueryProvider = ({ children }) => {
  // type
  const type = "firma";

  // state
  const [fetchAllSelected, setFetchAllSelected] = useState(false);
  const [selectAllTotalCount, setSelectAllTotalCount] = useState<number | null>(
    null,
  );
  const [selectAllError, setSelectAllError] = useState<ApolloError | undefined>(
    undefined,
  );
  const [query, setQuery] = useState<IPagedFirmaQuery>({
    page: 1,
  });
  const [emailableIds, setEmailableIds] = useState<Array<string>>([]);

  // localstorage state
  const [storedFilters, setStoredFilters] = useLocalStorageState<
    QueryFilter<FirmaFilterKeys>[]
  >("firmaFilters", { defaultValue: [] });
  const [sortOrder] = useLocalStorageState<Array<SortBy>>(
    firmaIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  // reducers
  const [queryFilters, updateFilters] = useReducer(
    queryFilterReducer,
    storedFilters,
  );

  // business
  const {
    addSelectedIds,
    clearSelection,
    removeSelectedIds,
    selectAll,
    selectedIds,
    lastSelectedId,
    setLastSelectedId,
  } = useTableSelection();

  const [columns, setActiveColumns] = useActiveColumns(
    "firmaindex-active-columns",
    defaultFirmaColumns,
  );

  const page = query.page ? query.page : 1;
  const { after, before } = query;

  const {
    data,
    error: selectAllQueryError,
    loading: fetchAllLoading,
  } = useQuery<IPagedFirmaResponse>(
    GET_FIRMA_IDS_AND_EMAIL(query, selectAllTotalCount ?? 0),
    {
      skip: !fetchAllSelected || !selectAllTotalCount,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  // effects
  useEffect(() => {
    setStoredFilters(queryFilters);
    setQuery({
      filters: buildFilter(queryFilters),
      order: {
        orderDirection: sortOrder[0].direction.toUpperCase() as OrderDirection,
        orderKey: sortOrder[0].field as PagedFirmaQueryField,
      },
    });
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters, setQuery, setStoredFilters, sortOrder]);

  useEffect(() => {
    if (!fetchAllSelected || fetchAllLoading || !data) return;

    const selectedIds = data.firmaer.edges.map(
      (edge) => edge.node.id,
    ) as Array<string>;
    selectAll(selectedIds);

    const allEmailableIds = data.firmaer.edges.flatMap((edge) => {
      if (edge.node.email) {
        return [edge.node.id as string];
      } else {
        return [];
      }
    });

    setEmailableIds(allEmailableIds);
    setFetchAllSelected(false);
    setSelectAllTotalCount(null);
  }, [data, fetchAllLoading, fetchAllSelected, selectAll]);

  useEffect(() => {
    if (selectAllQueryError && fetchAllSelected) {
      setSelectAllError(selectAllQueryError);
      setFetchAllSelected(false);
      setSelectAllTotalCount(null);
    }
  }, [selectAllQueryError, fetchAllSelected]);

  // query
  const currentPage = usePagedFirmaQuery(
    {
      after: after,
      before: before,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const nextPage = usePagedFirmaQuery(
    {
      after: currentPage.data?.firmaer?.pageInfo.endCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const prevPage = usePagedFirmaQuery(
    {
      before: currentPage.data?.firmaer?.pageInfo.startCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );
  const loading = currentPage.loading || nextPage.loading || prevPage.loading;

  // wait? another effect?!
  useEffect(() => {
    if (selectedIds.length) {
      const currentFirmaer = currentPage.data?.firmaer.edges.map(
        (edge) => edge.node as FirmaDto,
      );

      // find selected current personer
      const selectedCurrentFirmaer = currentFirmaer?.flatMap((firma) => {
        if (selectedIds.includes(firma.id)) {
          return [firma];
        } else {
          return [];
        }
      });

      // find selected emailable ids
      const selectedEmailableIds =
        selectedCurrentFirmaer?.flatMap((firma) => {
          if (firma.email) {
            return [firma.id];
          } else {
            return [];
          }
        }) || [];

      setEmailableIds((latest) => {
        // filter all current ids out of the emailable list
        const filteredEmailableIds = latest.filter(
          (id) => !currentFirmaer?.find((firma) => firma.id === id),
        );

        return union(filteredEmailableIds, selectedEmailableIds);
      });
    } else {
      setEmailableIds([]);
    }
  }, [currentPage.data?.firmaer.edges, selectedIds]);

  return (
    <PagedFirmaQueryContext.Provider
      value={{
        loading,
        error: currentPage.error,
        pagedResponse: currentPage?.data,
        columns,
        setActiveColumns,
        setQuery,
        query,
        page,
        nextPage,
        prevPage,
        refetch: currentPage.refetch,
        type,
        emailableIds,
        selectAllError,
        clearSelectAllError: () => setSelectAllError(undefined),
        selection: {
          addSelectedIds,
          clearSelection,
          removeSelectedIds,
          selectAll: (totalCount: number) => {
            setSelectAllError(undefined);
            setSelectAllTotalCount(totalCount);
            setFetchAllSelected(true);
          },
          selectedIds,
          lastSelectedId,
          setLastSelectedId,
        },
      }}
    >
      <FirmaFiltersContext.Provider value={queryFilters}>
        <FirmaFiltersEventContext.Provider value={updateFilters}>
          {children}
        </FirmaFiltersEventContext.Provider>
      </FirmaFiltersContext.Provider>
    </PagedFirmaQueryContext.Provider>
  );
};

function usePagedFirmaQuery(
  query: IPagedFirmaQuery,
  columns: IColumn<PagedFirmaQueryField>[],
) {
  return useQuery<IPagedFirmaResponse>(
    GET_PAGED_FIRMA_QUERY({
      after: query.after,
      before: query.before,
      fields: getActiveColumnFields(columns),
      filters: query.filters,
      order: query.order,
    }),
  );
}

const defaultSortBy: Array<SortBy<PagedFirmaQueryField>> = [
  {
    field: "navn1",
    direction: "asc",
  },
];
